// Captures inbound URL tracking parameters (utm_* and campaign_id) into
// sessionStorage on first visit within a browser session. The SPA router
// replaces the URL on navigation, so we must snapshot params before they
// disappear from window.location.search.

export interface InboundParams {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_term: string | null;
  utm_content: string | null;
  /** External campaign identifier passed by email / ad platforms. */
  campaign_id: string | null;
  /** Unix ms timestamp when params were first captured. */
  captured_at: number;
  /** Full original URL (pathname + search) at time of capture. */
  original_url: string;
}

const SESSION_PARAMS_KEY = 'bf_inbound_params';

// ---------- Safe sessionStorage helpers ----------
// sessionStorage.getItem / setItem can throw in environments where storage is
// blocked (private browsing with strict settings, sandboxed iframes, etc.).

export function safeSessionGet(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

export function safeSessionSet(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // Storage unavailable — silently ignore.
  }
}

export function safeSessionRemove(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // Storage unavailable — silently ignore.
  }
}

/** Returns true if at least one tracking field is non-null. */
export function hasAnyInboundParam(params: InboundParams): boolean {
  return (
    params.utm_source !== null ||
    params.utm_medium !== null ||
    params.utm_campaign !== null ||
    params.utm_term !== null ||
    params.utm_content !== null ||
    params.campaign_id !== null
  );
}

/**
 * Reads inbound tracking params from the current URL and writes them to
 * sessionStorage if:
 *  - at least one param is present, AND
 *  - sessionStorage does not already hold a snapshot for this session.
 *
 * Returns the captured params (new or existing), or null if no params are
 * present and none were previously stored.
 */
export function captureInboundParams(): InboundParams | null {
  if (typeof window === 'undefined') return null;

  // Already captured for this session — return the stored snapshot.
  const existing = safeSessionGet(SESSION_PARAMS_KEY);
  if (existing) {
    try {
      return JSON.parse(existing) as InboundParams;
    } catch {
      safeSessionRemove(SESSION_PARAMS_KEY);
    }
  }

  const p = new URLSearchParams(window.location.search);
  // Rebuild the query string with only the whitelisted tracking keys so that
  // sensitive params (auth tokens, email addresses, order IDs, etc.) are never
  // written to sessionStorage or forwarded into GTM events.
  const TRACKED_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'campaign_id'] as const;
  const sanitizedSearch = new URLSearchParams();
  for (const key of TRACKED_KEYS) {
    const val = p.get(key);
    if (val !== null) sanitizedSearch.set(key, val);
  }
  const sanitizedQuery = sanitizedSearch.toString();
  const params: InboundParams = {
    utm_source: p.get('utm_source'),
    utm_medium: p.get('utm_medium'),
    utm_campaign: p.get('utm_campaign'),
    utm_term: p.get('utm_term'),
    utm_content: p.get('utm_content'),
    campaign_id: p.get('campaign_id'),
    captured_at: Date.now(),
    original_url: window.location.pathname + (sanitizedQuery ? '?' + sanitizedQuery : ''),
  };

  if (!hasAnyInboundParam(params)) return null;

  safeSessionSet(SESSION_PARAMS_KEY, JSON.stringify(params));
  return params;
}

/** Returns the stored inbound params for this session, or null if none. */
export function getInboundParams(): InboundParams | null {
  if (typeof window === 'undefined') return null;
  const raw = safeSessionGet(SESSION_PARAMS_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as InboundParams;
  } catch {
    return null;
  }
}
